import { useState, useEffect } from 'react'
import { mediaApi } from '../../api/media'
import { messagesApi } from '../../api/messages'
import { useUiStore } from '../../stores/uiStore'
import { useAuthStore } from '../../stores/authStore'
import { parseFormatting } from '../../utils/formatText'
import { AudioPlayer } from '../ui/AudioPlayer'
import { VideoPlayer } from '../ui/VideoPlayer'
import { MediaAlbum } from './MediaAlbum'
import { LinkPreview } from './LinkPreview'
import { MessageReactions, AddReactionButton, QUICK_EMOJIS } from './MessageReactions'
import { Avatar } from '../ui/Avatar'
import { parseCallMessage, joinGroupCall } from '../../lib/groupCall'
import { useChatStore } from '../../stores/chatStore'
import { chatTitle } from '../../lib/displayName'
import type { Message } from '../../types'

interface Props {
  message: Message
  isMine: boolean
  senderName?: string | null
  senderAvatar?: string | null
  showSenderAvatar?: boolean
  replyMessage?: Message | null
  isGrouped?: boolean
  onReply: (quotedText?: string) => void
  onEdit: () => void
  onDelete: (forEveryone: boolean) => void
  onForward: () => void
  onPin?: () => void
  isSelected?: boolean
  selectMode?: boolean
  onSelect?: (id: string) => void
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function TTLCountdown({ createdAt, ttlSeconds, onExpire }: { createdAt: string; ttlSeconds: number; onExpire: () => void }) {
  const [remaining, setRemaining] = useState<number>(() => {
    const expiry = new Date(createdAt).getTime() + ttlSeconds * 1000
    return Math.max(0, Math.floor((expiry - Date.now()) / 1000))
  })

  useEffect(() => {
    if (remaining <= 0) { onExpire(); return }
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(t); onExpire(); return 0 }
        return r - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = remaining >= 3600
    ? `${Math.floor(remaining / 3600)}h`
    : remaining >= 60
      ? `${Math.floor(remaining / 60)}m`
      : `${remaining}s`

  return (
    <span className="inline-flex items-center gap-0.5 text-red-400 text-[11px] font-mono" title="Самоуничтожается">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <polyline points="12 7 12 12 15 14"/>
        <line x1="9" y1="3" x2="15" y2="3"/>
        <line x1="12" y1="1" x2="12" y2="3"/>
        <path d="M18.4 2.6 A 9 9 0 0 1 21 9"/>
      </svg>
      {fmt}
    </span>
  )
}

// Module-level caches: survive Virtuoso remounts, prevent re-render/re-fetch loops.
const mediaTypeCache = new Map<string, 'image' | 'audio' | 'video'>()
const mimeTypeCache = new Map<string, string>()
const fileNameCache = new Map<string, string>()
// Track IDs where thumbnail already failed so onError doesn't cycle on remount.
const thumbnailFailed = new Set<string>()
// Blob URL cache: once fetched, images display instantly on Virtuoso remounts.
const imageBlobCache = new Map<string, string>() // id -> blob URL

function fetchAndCacheImage(id: string, url: string): void {
  if (imageBlobCache.has(id)) return
  fetch(url)
    .then((r) => r.ok ? r.blob() : Promise.reject())
    .then((blob) => { imageBlobCache.set(id, URL.createObjectURL(blob)) })
    .catch(() => {})
}

function MediaAttachment({
  id, url, isSingle, onOpenMedia,
}: {
  id: string; url: string; isSingle: boolean; onOpenMedia: (url: string) => void
}) {
  const cached = mediaTypeCache.get(id)
  const [mediaType, setMediaType] = useState<'image' | 'audio' | 'video' | 'unknown'>(cached ?? 'unknown')
  const [mimeType, setMimeType] = useState<string>(mimeTypeCache.get(id) ?? '')
  const [fileName, setFileName] = useState<string>(fileNameCache.get(id) ?? '')
  // Initialize from module-level set so Virtuoso remounts show file card immediately.
  const [imgFailed, setImgFailed] = useState(() => thumbnailFailed.has(id))

  useEffect(() => {
    if (mediaTypeCache.has(id)) return
    mediaApi.getInfo(id)
      .then((info) => {
        const ct = info.mime_type ?? ''
        const fn = info.file_name ?? ''
        mimeTypeCache.set(id, ct)
        fileNameCache.set(id, fn)
        setMimeType(ct)
        setFileName(fn)
        const t: 'image' | 'audio' | 'video' = ct.startsWith('video/')
          ? 'video'
          : ct.startsWith('audio/')
            ? 'audio'
            : 'image'
        mediaTypeCache.set(id, t)
        setMediaType(t)
      })
      .catch(() => {
        mediaTypeCache.set(id, 'image')
        setMediaType('image')
      })
  }, [id])

  if (mediaType === 'unknown') {
    return <div className="w-48 h-24 rounded-lg bg-bg-input animate-pulse" />
  }
  if (mediaType === 'audio') {
    return <AudioPlayer src={url} />
  }
  if (mediaType === 'video') {
    return <VideoPlayer src={url} circle={isSingle} />
  }

  // HEIC/HEIF: most browsers can't render it natively — show a download card.
  // After the backend fix, new uploads will have JPEG thumbnails and render fine.
  // This card handles old uploads where no thumbnail was generated.
  const isHeic = mimeType.includes('heic') || mimeType.includes('heif')
  if (isHeic && imgFailed) {
    const displayName = fileName || 'photo.heic'
    return (
      <a
        href={url}
        download={displayName}
        className="flex items-center gap-3 px-3 py-2.5 bg-bg-input rounded-xl hover:bg-bg-hover transition-colors max-w-[260px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-9 h-9 rounded-lg bg-accent/15 flex items-center justify-center shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-txt-primary text-sm font-medium truncate">{displayName}</p>
          <p className="text-txt-meta text-xs">HEIC · нажмите для скачивания</p>
        </div>
      </a>
    )
  }

  const thumbnailUrl = mediaApi.getThumbnailURL(id, '800x800')
  // Use cached blob URL for instant display on Virtuoso remounts (no network flash).
  const cachedSrc = imageBlobCache.get(id)

  return (
    <button
      onClick={() => onOpenMedia(url)}
      className="block rounded-lg overflow-hidden max-w-full mb-1"
    >
      <img
        src={cachedSrc ?? thumbnailUrl}
        alt="media"
        className="max-w-full max-h-64 rounded-lg object-cover min-h-24"
        loading="eager"
        onLoad={(e) => {
          // On first successful load, fetch & cache as blob for instant future renders.
          const src = e.currentTarget.src
          if (!imageBlobCache.has(id) && !src.startsWith('blob:')) {
            fetchAndCacheImage(id, src)
          }
        }}
        onError={(e) => {
          if (!thumbnailFailed.has(id)) {
            // First failure: thumbnail 404 — try the original file URL once.
            thumbnailFailed.add(id)
            e.currentTarget.src = url
          } else {
            // Second failure: original also unrenderable (e.g. HEIC in Chrome).
            // Stop here — prevents infinite loop. Show file card for HEIC.
            e.currentTarget.style.display = 'none'
            if (isHeic) setImgFailed(true)
          }
        }}
      />
    </button>
  )
}

// Telegram-style sender colors (rotating palette for group chats)
const SENDER_COLORS = [
  'text-[#e17076]', // red
  'text-[#faa774]', // orange
  'text-[#a695e7]', // purple
  'text-[#76c6d0]', // cyan
  'text-[#5fad7a]', // green
  'text-[#ee6e97]', // pink
  'text-[#65b9f4]', // light-blue
]

function senderColor(senderId: string): string {
  let h = 0
  for (let i = 0; i < senderId.length; i++) h = (h * 31 + senderId.charCodeAt(i)) >>> 0
  return SENDER_COLORS[h % SENDER_COLORS.length]
}

export function MessageItem({ message, isMine, senderName, senderAvatar, showSenderAvatar, replyMessage, isGrouped, onReply, onEdit, onDelete, onForward, onPin, isSelected, selectMode, onSelect }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [selectedText, setSelectedText] = useState<string>('')
  const [translatedText, setTranslatedText] = useState<string | null>(null)
  const [translating, setTranslating] = useState(false)
  const [ttlExpired, setTtlExpired] = useState(false)
  const { openMedia } = useUiStore()
  const callMsg = parseCallMessage(message.text)
  const { userId } = useAuthStore()

  if (ttlExpired) {
    return (
      <div className="flex px-4 py-0.5 justify-start">
        <span className="text-txt-meta text-sm italic">⏱ Сообщение удалено</span>
      </div>
    )
  }

  const handleTranslate = async () => {
    setMenuOpen(false)
    if (translatedText) { setTranslatedText(null); return }
    if (!message.text) return
    setTranslating(true)
    try {
      const res = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(message.text)}&langpair=auto|en`)
      const data = await res.json()
      const translated = data?.responseData?.translatedText
      if (translated && translated !== message.text) setTranslatedText(translated)
    } catch { /* ignore */ }
    finally { setTranslating(false) }
  }

  if (message.deleted_for_all) {
    return (
      <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} px-4 py-0.5`}>
        <span className="text-txt-meta text-sm italic">Сообщение удалено</span>
      </div>
    )
  }

  // Whether this message is in a group chat (has sender name = is a group non-mine message)
  const isGroupChat = !!senderName || showSenderAvatar !== undefined

  return (
    <div
      className={`flex ${isMine ? 'justify-end' : 'justify-start'} ${isGroupChat && !isMine ? 'pl-2 pr-4' : 'px-4'} py-0.5 ${isGrouped ? 'pt-0' : 'pt-1'} group
        ${isSelected ? 'bg-accent/10' : ''} transition-colors`}
      onClick={selectMode ? () => onSelect?.(message.id) : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onContextMenu={(e) => {
        if (selectMode) { e.preventDefault(); onSelect?.(message.id); return }
        e.preventDefault()
        const sel = window.getSelection()
        setSelectedText(sel && sel.toString().trim().length > 0 ? sel.toString().trim() : '')
        setMenuOpen(true)
      }}
    >
      {/* Left avatar column for group non-mine messages */}
      {isGroupChat && !isMine && (
        <div className="w-8 shrink-0 self-end mr-1">
          {showSenderAvatar ? (
            <Avatar
              name={senderName ?? '?'}
              src={senderAvatar ?? undefined}
              size={28}
            />
          ) : null}
        </div>
      )}
      {selectMode && (
        <div className={`flex items-center pr-2 shrink-0 ${isMine ? 'order-last pl-2 pr-0' : ''}`}>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
            ${isSelected ? 'bg-accent border-accent' : 'border-txt-meta/40'}`}>
            {isSelected && (
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="2 6 5 9 10 3"/>
              </svg>
            )}
          </div>
        </div>
      )}
      <div className="relative max-w-[70%]">
        {/* Bubble */}
        <div
          className={`rounded-2xl px-3 py-2 shadow-sm
            ${isMine
              ? `bg-bg-sent text-txt-primary ${isGrouped ? '' : 'rounded-br-sm'}`
              : `bg-bg-message text-txt-primary ${isGrouped ? '' : 'rounded-bl-sm'}`
            }`}
        >
          {/* Sender name for non-mine messages */}
          {senderName && (
            <div className={`text-xs font-semibold mb-1 ${senderColor(message.sender_id)}`}>{senderName}</div>
          )}

          {/* Reply preview */}
          {message.reply_to_message_id && (
            <div
              className="border-l-2 border-accent pl-2 mb-1.5 rounded-sm bg-black/10 py-1 pr-2 cursor-pointer hover:bg-black/20 transition-colors"
              onClick={() => {
                if (message.reply_to_message_id) {
                  useUiStore.getState().setHighlightedMessage(message.reply_to_message_id)
                }
              }}
            >
              {replyMessage ? (
                <>
                  <p className={`text-xs font-medium mb-0.5 ${replyMessage.sender_id === userId ? 'text-accent' : senderColor(replyMessage.sender_id)}`}>
                    {replyMessage.sender_id === userId ? 'Вы' : 'Пользователь'}
                  </p>
                  <p className="text-txt-secondary text-xs line-clamp-2">
                    {replyMessage.media_ids?.length && !replyMessage.text ? '📎 Вложение' : (replyMessage.text || '...')}
                  </p>
                </>
              ) : (
                <p className="text-txt-secondary text-xs italic">Исходное сообщение удалено</p>
              )}
            </div>
          )}

          {/* Forward marker */}
          {message.forward_from_chat_id && (
            <div className="text-accent text-xs mb-1">↩ Переслано</div>
          )}

          {/* Media */}
          {(() => {
            const ids = message.media_ids
            if (!ids?.length) return null
            // Multiple images → album
            if (ids.length > 1) {
              return <MediaAlbum ids={ids} />
            }
            const id = ids[0]
            const url = mediaApi.getURL(id)
            return (
              <MediaAttachment
                key={id}
                id={id}
                url={url}
                isSingle={!message.text}
                onOpenMedia={openMedia}
              />
            )
          })()}

          {/* Приглашение в групповой звонок */}
          {callMsg ? (
            <div className="flex items-center gap-3 py-1">
              <span className="w-9 h-9 rounded-full bg-accent/15 flex items-center justify-center text-lg shrink-0">
                {callMsg.call_type === 'video' ? '🎥' : '📞'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-txt-primary">
                  {(isMine ? 'Вы' : senderName ?? 'Пользователь')} начал
                  {callMsg.call_type === 'video' ? ' видеозвонок' : ' аудиозвонок'}
                </p>
                <button
                  onClick={() => {
                    const chat = useChatStore.getState().chats.find((c) => c.id === message.chat_id)
                    joinGroupCall(message.chat_id, callMsg.call_type, chat ? chatTitle(chat) : 'Групповой звонок').catch(() => {})
                  }}
                  className="text-accent text-sm font-medium hover:opacity-80"
                >
                  Присоединиться к звонку
                </button>
              </div>
            </div>
          ) : (
          <>
          {/* Text */}
          {message.text && (
            <>
              <p className="text-[15px] leading-relaxed break-words whitespace-pre-wrap">
                {parseFormatting(message.text)}
              </p>
              {translating && (
                <p className="text-txt-meta text-xs mt-1 italic">Translating...</p>
              )}
              {translatedText && (
                <div className="mt-1.5 border-t border-divider/50 pt-1.5">
                  <p className="text-txt-meta text-[11px] mb-0.5">Translation (EN):</p>
                  <p className="text-txt-secondary text-sm leading-relaxed">{translatedText}</p>
                </div>
              )}
            </>
          )}

          {/* Link preview */}
          {(() => {
            if (!message.text) return null
            const match = message.text.match(/https?:\/\/[^\s<>"']+/)
            if (!match) return null
            return <LinkPreview url={match[0]} />
          })()}
          </>
          )}

          {/* Reaction badges stay inside the bubble */}
          <MessageReactions
            chatId={message.chat_id}
            messageId={message.id}
            reactions={message.reactions ?? []}
            isMine={isMine}
          />

          {/* Meta */}
          <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
            {message.ttl_seconds > 0 && (
              <TTLCountdown
                createdAt={message.created_at}
                ttlSeconds={message.ttl_seconds}
                onExpire={() => setTtlExpired(true)}
              />
            )}
            <span className="text-txt-meta text-[11px]">
              {formatTime(message.created_at)}
            </span>
            {message.edited_at && (
              <span className="text-txt-meta text-[11px]">ред.</span>
            )}
            {isMine && (
              <span className="ml-0.5 inline-flex items-center text-txt-meta" title={message.pending ? 'Отправка...' : message.read_by?.length ? 'Прочитано' : 'Доставлено'}>
                {message.pending ? (
                  // Clock — sending
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="text-txt-meta opacity-60">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : message.read_by && message.read_by.length > 0 ? (
                  // Double check — read (две одинаковые галочки со смещением)
                  <svg width="17" height="10" viewBox="0 0 18 10" fill="none" className="text-accent">
                    <path d="M1 5L4 8L9.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 5L9 8L14.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  // Single check — delivered
                  <svg width="11" height="10" viewBox="0 0 11 10" fill="none" className="text-txt-meta">
                    <path d="M1 5L4 8L10 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Floating reaction button — positioned outside the bubble, does not affect bubble size */}
        <AddReactionButton
          chatId={message.chat_id}
          messageId={message.id}
          reactions={message.reactions ?? []}
          isMine={isMine}
          visible={hovered}
        />

        {/* Context menu */}
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div className={`absolute z-50 bottom-full mb-1 ${isMine ? 'right-0' : 'left-0'} flex flex-col gap-1 animate-fade-in`}>
              {/* Quick reaction bar */}
              <div className="bg-bg-primary border border-divider rounded-2xl shadow-lg px-2 py-1.5 flex gap-0.5">
                {QUICK_EMOJIS.map((emoji) => {
                  const reacted = message.reactions?.some(
                    (r) => r.emoji === emoji && r.user_ids.includes(useAuthStore.getState().userId ?? '')
                  )
                  return (
                    <button
                      key={emoji}
                      onClick={async () => {
                        setMenuOpen(false)
                        try {
                          if (reacted) {
                            await messagesApi.unreact(message.chat_id, message.id, emoji)
                          } else {
                            await messagesApi.react(message.chat_id, message.id, emoji)
                          }
                        } catch { /* ignore */ }
                      }}
                      className={`text-xl w-8 h-8 flex items-center justify-center rounded-xl
                                  hover:bg-bg-hover transition-colors ${reacted ? 'bg-accent/15' : ''}`}
                    >
                      {emoji}
                    </button>
                  )
                })}
              </div>
              <div className="bg-bg-input rounded-xl shadow-xl py-1 min-w-[180px] overflow-hidden">
                {[
                  ...(message.text ? [
                    {
                      label: 'Копировать',
                      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
                      action: () => { navigator.clipboard.writeText(message.text); setMenuOpen(false) },
                    },
                    {
                      label: translatedText ? 'Скрыть перевод' : 'Перевести',
                      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8l6 6"/><path d="M4 14l6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="M22 22l-5-10-5 10"/><path d="M14 18h6"/></svg>,
                      action: handleTranslate,
                    },
                  ] : []),
                  {
                    label: 'Ответить',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>,
                    action: () => { onReply(); setMenuOpen(false) },
                  },
                  ...(selectedText ? [{
                    label: 'Цитировать',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>,
                    action: () => { onReply(selectedText); setMenuOpen(false) },
                  }] : []),
                  {
                    label: 'Переслать',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 0 1 4-4h12"/></svg>,
                    action: () => { onForward(); setMenuOpen(false) },
                  },
                  ...(onSelect ? [{
                    label: 'Выбрать',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
                    action: () => { onSelect(message.id); setMenuOpen(false) },
                  }] : []),
                  ...(onPin ? [{
                    label: message.is_pinned ? 'Открепить' : 'Закрепить',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>,
                    action: () => { onPin?.(); setMenuOpen(false) },
                  }] : []),
                  ...(isMine ? [
                    {
                      label: 'Изменить',
                      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
                      action: () => { onEdit(); setMenuOpen(false) },
                    },
                    {
                      label: 'Удалить у всех',
                      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
                      action: () => { onDelete(true); setMenuOpen(false) },
                      danger: true,
                    },
                  ] : []),
                  {
                    label: 'Удалить у меня',
                    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
                    action: () => { onDelete(false); setMenuOpen(false) },
                    danger: true,
                  },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-bg-hover
                      ${'danger' in item && item.danger ? 'text-red-400' : 'text-txt-primary'}`}
                  >
                    <span className="shrink-0 opacity-70">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
